import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchTopicQuizItems,
  submitReviewAnswer,
  startQuizSession,
  updateQuizSession,
  completeQuizSession,
  getActiveSession,
  resetTopicProgress,
  synthesizeSpeech,
  isAudioPlaybackAllowed,
  fetchGrammarQuestions,
  fetchVerbFormQuestions,
  addWordBankEntry,
} from "../api";

const API_BASE = process.env.REACT_APP_API_URL || "";
const getToken = () => localStorage.getItem("token");

async function fetchQuestion(reviewItemId, type) {
  const token = getToken();
  const res = await fetch(
    `${API_BASE}/api/learning/items/${reviewItemId}/question/?type=${encodeURIComponent(
      type
    )}`,
    { headers: token ? { Authorization: `Token ${token}` } : {} }
  );
  if (!res.ok) throw new Error("Failed to generate question");
  return res.json();
}

function normalizeToken(t) {
  return (t || "").trim();
}

function arraysEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (normalizeToken(a[i]) !== normalizeToken(b[i])) return false;
  }
  return true;
}

/**
 * TopicQuiz
 *
 * Props:
 *   topicId        – ID of the topic to quiz
 *   quizMode       – "learn" | "review"  (default "learn")
 *   topicBestScore – current best score % for this topic (used to warn on restart)
 *   onExit         – called when the user exits or finishes
 *   onComplete     – called with { session_id, score_percent } when session completes
 *   onProgress     – called after every answered question so the parent can refresh progress
 */
export default function TopicQuiz({
  topicId,
  topicName,
  quizMode = "learn",
  topicBestScore = 0,
  onExit,
  onComplete,
  onProgress,
}) {
  // Tiny click sound feedback
  function playClickSound() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 440;
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.08);
    } catch (_) {}
  }

  // ── Pre-quiz dialog ────────────────────────────────────────────────
  // "checking"  → loading active session from backend
  // "resume"    → found incomplete session; offer Resume / Start Fresh
  // "warn"      → topic already completed; warn before starting fresh
  // "ready"     → start the quiz immediately (no dialog needed)
  const [preQuizPhase, setPreQuizPhase] = useState("checking");
  const [activeSessionData, setActiveSessionData] = useState(null); // saved session from backend
  // Capture topicBestScore at mount time so live progress updates don't re-trigger the pre-quiz dialog.
  const initialBestScoreRef = useRef(topicBestScore);

  // ── Question history ───────────────────────────────────────────────
  // Keyed by quiz index. Stores the question shown and the user's answer so
  // navigating back (Prev) shows the exact same question read-only.
  // { [index]: { questionObj, type, userAnswer, isCorrect, wasSkipped } }
  const [questionHistory, setQuestionHistory] = useState({});
  const questionHistoryRef = useRef({}); // read inside effects without stale closures

  // The furthest quiz index the user has organically reached by answering questions
  // and pressing Next. isViewingHistory is true for any index below this mark that
  // also has a saved history entry — whether the user is navigating backward OR
  // forward through already-answered questions.
  // Updated in goNext; reset to 0 (or resume index) in loadItems.
  const [highWaterMark, setHighWaterMark] = useState(0);
  const highWaterMarkRef = useRef(0);

  const saveToHistory = useCallback((idx, entry) => {
    const updated = { ...questionHistoryRef.current, [idx]: entry };
    questionHistoryRef.current = updated;
    setQuestionHistory(updated);
  }, []);

  // Auto-advance timer ref — cancelled when user manually navigates.
  const autoAdvanceTimerRef = useRef(null);

  // ── Items ──────────────────────────────────────────────────────────
  const [loadingItems, setLoadingItems] = useState(false);
  const [items, setItems] = useState([]);
  const [itemsError, setItemsError] = useState(null);
  const [index, setIndex] = useState(0);

  // ── Question ───────────────────────────────────────────────────────
  const [questionLoading, setQuestionLoading] = useState(false);
  const [questionError, setQuestionError] = useState(null);
  const [questionObj, setQuestionObj] = useState(null);

  // ── Answer UI state ────────────────────────────────────────────────
  // word_meaning: which MC option the user has tapped
  const [selectedChoice, setSelectedChoice] = useState(null);
  // word_meaning: whether the "Reveal Answer" panel is open
  const [revealed, setRevealed] = useState(false);
  // word_meaning: whether the user has already submitted this question
  const [choiceSubmitted, setChoiceSubmitted] = useState(false);

  // sentence_build: tokens assembled by the user (build mode = in display
  // order; cloze mode = filled into gap_indices in gap order)
  const [builtTokens, setBuiltTokens] = useState([]);
  // sentence_build: index in q.word_bank of each placed token (parallel to
  // builtTokens). Used to grey out already-used bank chips.
  const [builtBankIndices, setBuiltBankIndices] = useState([]);
  // sentence_build: whether the correct answer is being shown
  const [sentenceRevealed, setSentenceRevealed] = useState(false);

  const [feedback, setFeedback] = useState(null); // { ok: bool, message: str }

  // ── Session tracking ───────────────────────────────────────────────
  const [, setSessionId] = useState(null);
  const sessionRef = useRef({ seen: 0, correct: 0 });
  // Separately track grammar drill answers so they contribute to the final score.
  const grammarScoreRef = useRef({ seen: 0, correct: 0 });
  // Verb form drill answers — combined into final score but not persisted to backend session.
  const verbFormScoreRef = useRef({ seen: 0, correct: 0 });
  // Refs so callbacks always read the latest values without stale closures.
  const sessionIdRef = useRef(null);
  const totalItemsRef = useRef(0);
  // Count of regular (SRS) items only — used to cap current_index in session persistence
  // so verb-form items (appended after regular items) don't corrupt the resume index.
  const regularItemCountRef = useRef(0);

  // ── TTS ────────────────────────────────────────────────────────────
  const [ttsLoadingKey, setTtsLoadingKey] = useState(null);
  const [isPlayingAllForms, setIsPlayingAllForms] = useState(false);
  const quizAudioRef = useRef(null);

  const playArabic = useCallback(async (text, key) => {
    if (quizAudioRef.current) {
      quizAudioRef.current.pause();
      quizAudioRef.current = null;
    }
    if (ttsLoadingKey === key) {
      setTtsLoadingKey(null);
      return;
    }
    setTtsLoadingKey(key);
    try {
      const data = await synthesizeSpeech(text, ""); // empty speaker → default male voice
      setTtsLoadingKey(null);
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_content}`);
      quizAudioRef.current = audio;
      audio.onended = () => { quizAudioRef.current = null; };
      audio.onerror = () => { quizAudioRef.current = null; };
      audio.play().catch(() => { quizAudioRef.current = null; });
    } catch {
      setTtsLoadingKey(null);
    }
  }, [ttsLoadingKey]);

  // Plays an ordered list of Arabic strings sequentially via TTS.
  // Used for the "Play all forms" button on the last verb form question.
  const playAllVerbForms = useCallback(async (texts) => {
    if (isPlayingAllForms) return;
    // Stop any currently playing audio first.
    if (quizAudioRef.current) {
      quizAudioRef.current.pause();
      quizAudioRef.current = null;
    }
    setIsPlayingAllForms(true);
    try {
      for (const text of texts) {
        if (!text) continue;
        const data = await synthesizeSpeech(text, "");
        if (!data?.audio_content) continue;
        await new Promise((resolve) => {
          const audio = new Audio(`data:audio/mp3;base64,${data.audio_content}`);
          quizAudioRef.current = audio;
          audio.onended = () => { quizAudioRef.current = null; resolve(); };
          audio.onerror = () => { quizAudioRef.current = null; resolve(); };
          audio.play().catch(() => { quizAudioRef.current = null; resolve(); });
        });
        // Short gap between words.
        await new Promise((r) => setTimeout(r, 300));
      }
    } finally {
      setIsPlayingAllForms(false);
    }
  }, [isPlayingAllForms]);

  // ── Session complete screen ────────────────────────────────────────
  const [sessionDone, setSessionDone] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  // Per-word "add to Word Bank" state on the results screen, keyed by word_id:
  // "adding" | "added" | { error: "message" }
  const [bankState, setBankState] = useState({});

  // Add a missed quiz word to the Word Bank so the learner can revisit it.
  const handleAddToBank = useCallback(async (wordId) => {
    if (!wordId || bankState[wordId] === "adding" || bankState[wordId] === "added") return;
    setBankState((s) => ({ ...s, [wordId]: "adding" }));
    try {
      const res = await addWordBankEntry({ word_id: wordId });
      // Treat an existing entry as success — the word is in the bank either way.
      setBankState((s) => ({ ...s, [wordId]: "added" }));
      return res;
    } catch (e) {
      setBankState((s) => ({ ...s, [wordId]: { error: e.message || "Couldn't add" } }));
    }
  }, [bankState]);

  // ── Grammar drill phase ────────────────────────────────────────────
  // Shown before the SRS quiz on fresh starts for topics with a grammar rule.
  // grammarItems: null = still loading, [] = no questions, [...] = questions array
  const [grammarItems, setGrammarItems] = useState(null);
  const [grammarInfo, setGrammarInfo] = useState(null);   // { code, name, arabic, explanation }
  // "idle" | "active" | "done"
  const [grammarPhase, setGrammarPhase] = useState("idle");
  const [grammarIndex, setGrammarIndex] = useState(0);
  const [grammarChoice, setGrammarChoice] = useState(null);  // index of selected option
  const [grammarSubmitted, setGrammarSubmitted] = useState(false);

  // Fetch grammar questions once on mount
  useEffect(() => {
    if (!topicId) { setGrammarItems([]); return; }
    fetchGrammarQuestions(topicId)
      .then((data) => {
        setGrammarItems(data.questions || []);
        setGrammarInfo(data.rule || null);
      })
      .catch(() => setGrammarItems([]));
    // fetchGrammarQuestions is a stable module-level import; only topicId matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId]);

  const currentItem = items[index] || null;

  const desiredQuestionType = useMemo(() => {
    if (!currentItem) return null;
    if (currentItem.item_type === "verb_form") return "verb_forms";
    return currentItem.item_type === "sentence" ? "sentence_build" : "word_meaning";
  }, [currentItem]);

  // ── Reset answer UI ────────────────────────────────────────────────
  const resetAnswerUI = () => {
    setSelectedChoice(null);
    setRevealed(false);
    setChoiceSubmitted(false);
    setBuiltTokens([]);
    setBuiltBankIndices([]);
    setSentenceRevealed(false);
    setFeedback(null);
  };

  // ── Pre-quiz check: look for an active (incomplete) session ───────
  useEffect(() => {
    if (!topicId) return;
    let cancelled = false;
    setPreQuizPhase("checking");
    setActiveSessionData(null);

    getActiveSession(topicId)
      .then((data) => {
        if (cancelled) return;
        if (data.session && data.session.item_order?.length > 0 && data.session.current_index > 0) {
          // There is a resumable session with saved progress
          setActiveSessionData(data.session);
          setPreQuizPhase("resume");
        } else if (initialBestScoreRef.current >= 80 && quizMode === "learn") {
          // Topic already completed — warn before starting fresh
          setPreQuizPhase("warn");
        } else {
          setPreQuizPhase("ready");
        }
      })
      .catch(() => {
        if (!cancelled) setPreQuizPhase("ready");
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, quizMode]);

  // ── Load items + start session ─────────────────────────────────────
  // resumeSession: active session object to resume, or null for a fresh start.
  const loadItems = useCallback(async (resumeSession = null) => {
    try {
      setLoadingItems(true);
      setItemsError(null);
      setItems([]);
      setIndex(0);
      setSessionId(null);
      sessionRef.current = { seen: 0, correct: 0 };
      verbFormScoreRef.current = { seen: 0, correct: 0 };
      sessionIdRef.current = null;
      totalItemsRef.current = 0;
      regularItemCountRef.current = 0;
      setQuestionObj(null);
      setSessionDone(false);
      setFinalScore(null);
      setBankState({});
      questionHistoryRef.current = {};
      setQuestionHistory({});
      highWaterMarkRef.current = 0;
      setHighWaterMark(0);
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
      resetAnswerUI();

      if (resumeSession) {
        // Resume: fetch exactly the saved items by ID so we always get the
        // full original set regardless of SRS state on this device.
        const data = await fetchTopicQuizItems(topicId, quizMode, 50, resumeSession.item_order);
        const allById = Object.fromEntries((data.items || []).map((it) => [it.id, it]));
        // Preserve the original order (response is already ordered but map for safety).
        const orderedItems = resumeSession.item_order
          .map((id) => allById[id])
          .filter(Boolean);

        // Fetch verb form questions and append as synthetic items (non-fatal).
        const vfData = await fetchVerbFormQuestions(topicId).catch(() => ({ questions: [] }));
        const vfItems = (vfData.questions || []).map((q) => ({
          id: `vf_${q.verb_id}_${q.target_form}`,
          item_type: "verb_form",
          verb_form_question: q,
        }));

        setItems([...orderedItems, ...vfItems]);
        totalItemsRef.current = orderedItems.length;
        regularItemCountRef.current = orderedItems.length;
        setIndex(resumeSession.current_index);
        // All questions before the resume point are considered "already answered"
        // so navigating back to them shows them as read-only history.
        highWaterMarkRef.current = resumeSession.current_index;
        setHighWaterMark(resumeSession.current_index);
        sessionRef.current = {
          seen: resumeSession.items_seen,
          correct: resumeSession.items_correct,
        };
        setSessionId(resumeSession.id);
        sessionIdRef.current = resumeSession.id;

        // Restore question history from the server session (works across devices).
        if (resumeSession.question_history && Object.keys(resumeSession.question_history).length > 0) {
          questionHistoryRef.current = resumeSession.question_history;
          setQuestionHistory(resumeSession.question_history);
        }
      } else {
        // Fresh start: fetch items and create a new session.
        const data = await fetchTopicQuizItems(topicId, quizMode, 50);
        const regularItems = data.items || [];
        const itemOrder = regularItems.map((it) => it.id);

        // Fetch verb form questions and append as synthetic items (non-fatal).
        const vfData = await fetchVerbFormQuestions(topicId).catch(() => ({ questions: [] }));
        const vfItems = (vfData.questions || []).map((q) => ({
          id: `vf_${q.verb_id}_${q.target_form}`,
          item_type: "verb_form",
          verb_form_question: q,
        }));

        const session = await startQuizSession(topicId, quizMode, itemOrder).catch(() => null);

        setItems([...regularItems, ...vfItems]);
        totalItemsRef.current = regularItems.length;
        regularItemCountRef.current = regularItems.length;
        if (session) {
          setSessionId(session.id);
          sessionIdRef.current = session.id;
        }
      }
    } catch (e) {
      console.error(e);
      setItemsError(e.message || "Failed to load quiz items");
    } finally {
      setLoadingItems(false);
    }
  }, [topicId, quizMode]);

  // ── Auto-start when pre-quiz resolves to "ready" (no dialog needed) ──
  // If the topic has grammar questions, show the grammar drill first; otherwise
  // go straight to the SRS quiz.  We wait until grammarItems has loaded (not null).
  useEffect(() => {
    if (preQuizPhase !== "ready") return;
    if (grammarItems === null) return; // grammar questions still loading
    if (grammarItems.length > 0) {
      setGrammarPhase("active");
      setGrammarIndex(0);
      setGrammarChoice(null);
      setGrammarSubmitted(false);
    } else {
      loadItems(null);
    }
  }, [preQuizPhase, grammarItems, loadItems]);

  // ── When grammar phase finishes, start the SRS quiz ───────────────
  useEffect(() => {
    if (grammarPhase === "done") {
      loadItems(null);
    }
  }, [grammarPhase, loadItems]);

  // ── Load question when item changes ───────────────────────────────
  useEffect(() => {
    if (!currentItem || !desiredQuestionType) return;

    // Verb form items carry their question data inline — no API fetch needed.
    if (currentItem.item_type === "verb_form") {
      setQuestionLoading(false);
      setQuestionError(null);
      const vfq = currentItem.verb_form_question;
      const syntheticQ = {
        review_item_id: currentItem.id,
        question: {
          question_type: "verb_forms",
          verb_id: vfq.verb_id,
          past: vfq.past,
          past_transliteration: vfq.past_transliteration,
          target_form: vfq.target_form,
          target_label_ar: vfq.target_label_ar,
          target_label_en: vfq.target_label_en,
          choices: vfq.choices,
          correct_answer: { form: vfq.correct_form },
        },
      };

      // Restore from history if already answered.
      const savedEntry = questionHistoryRef.current[index];
      const restoreFromHistory = savedEntry && !savedEntry.pending && index < highWaterMarkRef.current;
      if (restoreFromHistory) {
        setQuestionObj(syntheticQ);
        resetAnswerUI();
        if (savedEntry.wasSkipped) {
          setFeedback({ ok: false, message: "⏭ Skipped" });
        } else {
          setSelectedChoice(savedEntry.userAnswer);
          setChoiceSubmitted(true);
          setFeedback({ ok: savedEntry.isCorrect, message: savedEntry.isCorrect ? "✅ Correct!" : "❌ Not quite" });
        }
        return;
      }

      setQuestionObj(syntheticQ);
      resetAnswerUI();

      // Save as pending so the same question appears on resume.
      if (sessionIdRef.current && !questionHistoryRef.current[index]) {
        const pendingEntry = { questionObj: syntheticQ, type: "verb_forms", pending: true, wasSkipped: false, userAnswer: null, isCorrect: null };
        questionHistoryRef.current = { ...questionHistoryRef.current, [index]: pendingEntry };
        setQuestionHistory({ ...questionHistoryRef.current });
        updateQuizSession(sessionIdRef.current, { question_history: questionHistoryRef.current })
          .catch(() => {});
      }
      return;
    }

    // Restore from history only for already-answered questions.
    // Pending (unanswered) questions always re-fetch so that any admin edits
    // (e.g. adding a missing definition) are reflected immediately.
    const savedEntry = questionHistoryRef.current[index];
    const restoreFromHistory = savedEntry && !savedEntry.pending && index < highWaterMarkRef.current;
    if (restoreFromHistory) {
      setQuestionLoading(false);
      setQuestionError(null);
      setQuestionObj(savedEntry.questionObj);
      resetAnswerUI();
      if (!savedEntry.pending) {
        // Fully answered — restore feedback state so it's read-only.
        if (savedEntry.wasSkipped) {
          setFeedback({ ok: false, message: "⏭ Skipped" });
        } else if (savedEntry.type === "word_meaning") {
          setSelectedChoice(savedEntry.userAnswer);
          setChoiceSubmitted(true);
          setFeedback({ ok: savedEntry.isCorrect, message: savedEntry.isCorrect ? "✅ Correct!" : "❌ Not quite" });
        } else {
          setBuiltTokens(savedEntry.userAnswer || []);
          setFeedback({ ok: savedEntry.isCorrect, message: savedEntry.isCorrect ? "✅ Correct!" : "❌ Not quite" });
        }
      }
      return;
    }

    (async () => {
      try {
        setQuestionLoading(true);
        setQuestionError(null);
        setQuestionObj(null);
        resetAnswerUI();

        const data = await fetchQuestion(currentItem.id, desiredQuestionType);
        setQuestionObj(data);

        // Save as a pending (unanswered) entry immediately so the same question
        // is shown if the user switches devices before answering.
        if (sessionIdRef.current && !questionHistoryRef.current[index]) {
          const pendingEntry = { questionObj: data, type: desiredQuestionType, pending: true, wasSkipped: false, userAnswer: null, isCorrect: null };
          questionHistoryRef.current = { ...questionHistoryRef.current, [index]: pendingEntry };
          setQuestionHistory({ ...questionHistoryRef.current });
          updateQuizSession(sessionIdRef.current, { question_history: questionHistoryRef.current })
            .catch(() => {});
        }
      } catch (e) {
        console.error(e);
        setQuestionError(e.message || "Failed to load question");
      } finally {
        setQuestionLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem?.id, desiredQuestionType, index]);

  // ── Finish session ─────────────────────────────────────────────────
  const finishSession = useCallback(
    async () => {
      const { correct } = sessionRef.current;
      const totalItems = totalItemsRef.current;
      // Grammar and verb-form drill answers are tracked separately and combined
      // here so the final score reflects the full quiz.
      const gSeen = grammarScoreRef.current.seen;
      const gCorrect = grammarScoreRef.current.correct;
      const vfSeen = verbFormScoreRef.current.seen;
      const vfCorrect = verbFormScoreRef.current.correct;

      let result = { score_percent: 0, items_seen: totalItems, items_correct: correct, passed: false };

      if (sessionIdRef.current) {
        try {
          await updateQuizSession(sessionIdRef.current, {
            items_seen: totalItems,
            items_correct: correct,
            current_index: totalItems, // marks quiz as fully traversed
          });
          const completed = await completeQuizSession(sessionIdRef.current);
          // Merge grammar + verb-form results into the backend vocab score.
          const combinedSeen = (completed.items_seen || 0) + gSeen + vfSeen;
          const combinedCorrect = (completed.items_correct || 0) + gCorrect + vfCorrect;
          const combinedPercent = combinedSeen > 0
            ? Math.round((combinedCorrect / combinedSeen) * 100)
            : completed.score_percent;
          result = {
            score_percent: combinedPercent,
            items_seen: combinedSeen,
            items_correct: combinedCorrect,
            // Authoritative pass/fail from the backend (gates topic completion).
            passed: !!completed.passed,
          };
        } catch (e) {
          console.error("Failed to complete quiz session", e);
        }
      } else if (gSeen > 0 || vfSeen > 0) {
        // No backend session — combine totals from grammar + verb-form drills.
        const combinedSeen = totalItems + gSeen + vfSeen;
        const combinedCorrect = correct + gCorrect + vfCorrect;
        const combinedPercent = combinedSeen > 0 ? Math.round((combinedCorrect / combinedSeen) * 100) : 0;
        result = {
          score_percent: combinedPercent,
          items_seen: combinedSeen,
          items_correct: combinedCorrect,
          passed: combinedPercent >= 80,
        };
      }

      setFinalScore(result);
      setSessionDone(true);

      if (onComplete) {
        onComplete({ session_id: sessionIdRef.current, ...result });
      }
    },
    [onComplete]
  );

  // Review-item ids the user got wrong (or skipped) this run. Only regular
  // review items (numeric ids) count toward the backend completion gate, so
  // those are what a "retake missed" focuses on.
  const missedItemIds = items
    .filter((it, i) => typeof it?.id === "number" && questionHistory[i] && questionHistory[i].isCorrect === false)
    .map((it) => it.id);

  // Per-question breakdown for the results screen. Built from the main quiz
  // items joined with their answer history. Grammar warm-up questions are
  // tracked as aggregate counts only, so they don't appear as individual rows.
  const quizResults = items
    .map((it, i) => {
      const entry = questionHistory[i];
      if (!entry) return null; // unanswered (shouldn't happen once done)
      const q = entry.questionObj?.question || {};
      const isWord = it?.item_type === "word";

      // Each question type stores its prompt and answers differently, so pull
      // the display fields per type — otherwise verb-form rows (which have no
      // `.prompt` object) render blank.
      let arabic = "";
      let transliteration = "";
      let english = "";
      let userAnswerText = "";
      let correctAnswerText = "";
      let answerIsArabic = false;

      if (entry.type === "verb_forms") {
        // Prompt is the past-tense base + the target form label; choices carry
        // the Arabic text for each form key.
        const choices = q.choices || [];
        const formText = (form) => choices.find((c) => c.form === form)?.text || "";
        arabic = q.past || it?.payload?.text || "";
        transliteration = q.past_transliteration || "";
        english = q.target_label_en ? `Give the ${q.target_label_en}` : "";
        userAnswerText = entry.wasSkipped ? "" : formText(entry.userAnswer);
        correctAnswerText = formText(q.correct_answer?.form);
        answerIsArabic = true;
      } else if (entry.type === "sentence_build") {
        const prompt = q.prompt || {};
        english = prompt.english || it?.payload?.translation || "";
        userAnswerText = Array.isArray(entry.userAnswer) ? entry.userAnswer.join(" ") : "";
        correctAnswerText = (q.correct_answer?.tokens || []).join(" ");
        answerIsArabic = true;
      } else {
        // word_meaning — Arabic word prompt, English meaning choices.
        const prompt = q.prompt || {};
        arabic = it?.payload?.text || prompt.arabic || "";
        transliteration = it?.payload?.transliteration || prompt.transliteration || "";
        english = prompt.english || it?.payload?.translation || "";
        userAnswerText = entry.wasSkipped ? "" : (entry.userAnswer || "");
        correctAnswerText = q.correct_answer?.choice || "";
      }

      return {
        key: `${it?.id ?? "q"}-${i}`,
        arabic,
        transliteration,
        english,
        isCorrect: entry.isCorrect === true,
        wasSkipped: !!entry.wasSkipped,
        userAnswerText,
        correctAnswerText,
        answerIsArabic,
        // Only vocabulary words can be added back to the Word Bank.
        wordId: isWord ? it?.payload?.word_id || null : null,
      };
    })
    .filter(Boolean);

  // ── Retake only the items missed this run ──────────────────────────
  const handleRetakeMissed = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return;
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    // Reset run state (mirrors loadItems) — no grammar / verb-form drills here.
    setLoadingItems(true);
    setItemsError(null);
    setItems([]);
    setIndex(0);
    setSessionId(null);
    sessionRef.current = { seen: 0, correct: 0 };
    grammarScoreRef.current = { seen: 0, correct: 0 };
    verbFormScoreRef.current = { seen: 0, correct: 0 };
    sessionIdRef.current = null;
    totalItemsRef.current = 0;
    regularItemCountRef.current = 0;
    setQuestionObj(null);
    setSessionDone(false);
    setFinalScore(null);
    setBankState({});
    questionHistoryRef.current = {};
    setQuestionHistory({});
    highWaterMarkRef.current = 0;
    setHighWaterMark(0);
    resetAnswerUI();
    try {
      const data = await fetchTopicQuizItems(topicId, quizMode, 50, ids);
      const allById = Object.fromEntries((data.items || []).map((it) => [it.id, it]));
      const orderedItems = ids.map((id) => allById[id]).filter(Boolean);
      const session = await startQuizSession(
        topicId,
        quizMode,
        orderedItems.map((it) => it.id)
      ).catch(() => null);
      setItems(orderedItems);
      totalItemsRef.current = orderedItems.length;
      regularItemCountRef.current = orderedItems.length;
      if (session) {
        setSessionId(session.id);
        sessionIdRef.current = session.id;
      }
    } catch (e) {
      console.error(e);
      setItemsError(e.message || "Failed to load quiz items");
    } finally {
      setLoadingItems(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, quizMode]);

  // ── Navigation helpers ─────────────────────────────────────────────
  const goNext = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    // Advance the high water mark so the question just answered is always
    // treated as read-only history when navigated back to.
    const nextIdx = index + 1;
    const newHWM = Math.max(highWaterMarkRef.current, nextIdx);
    highWaterMarkRef.current = newHWM;
    setHighWaterMark(newHWM);
    resetAnswerUI();
    setQuestionObj(null);
    if (nextIdx < items.length) {
      setIndex(nextIdx);
    } else {
      finishSession();
    }
  }, [index, items.length, finishSession]);

  // ── Persist session state after each answer ────────────────────────
  // Fire-and-forget: saves current_index + score so progress survives refresh.
  // current_index is capped at regularItemCountRef.current so verb-form item
  // positions are never persisted to the backend session.
  const persistSessionProgress = useCallback((nextIndex) => {
    if (!sessionIdRef.current) return;
    const cappedIndex = Math.min(nextIndex, regularItemCountRef.current);
    updateQuizSession(sessionIdRef.current, {
      items_seen: totalItemsRef.current,
      items_correct: sessionRef.current.correct,
      current_index: cappedIndex,
      question_history: questionHistoryRef.current,
    }).catch((e) => console.error("Session update failed", e));
    if (onProgress) onProgress();
  }, [onProgress]);

  // ── Skip (counts as wrong) ─────────────────────────────────────────
  const handleSkip = useCallback(async () => {
    if (questionObj?.question) {
      const q = questionObj.question;

      if (q.question_type === "verb_forms") {
        // Verb form items: no SRS submit — track locally only.
        verbFormScoreRef.current.seen += 1;
        saveToHistory(index, { questionObj, type: "verb_forms", userAnswer: null, isCorrect: false, wasSkipped: true });
        persistSessionProgress(index + 1);
      } else {
        sessionRef.current.seen += 1;
        // Don't increment correct — skip is treated as wrong
        try {
          await submitReviewAnswer({
            review_item_id: questionObj.review_item_id,
            question_type: q.question_type,
            is_correct: false,
            user_answer: {},
            correct_answer: q.correct_answer || {},
            // Quiz is a pure test — never change the word's learned state.
            update_known: false,
          });
        } catch (e) {
          console.error("Skip submit failed", e);
        }
        // ── Fix 1: saveToHistory BEFORE persistSessionProgress so the current
        // question's entry is included in the PATCH sent to the backend.
        saveToHistory(index, { questionObj, type: q.question_type, userAnswer: null, isCorrect: false, wasSkipped: true });
        persistSessionProgress(index + 1);
      }
    }
    goNext();
  }, [questionObj, goNext, persistSessionProgress, index, saveToHistory]);

  const goPrev = () => {
    if (index === 0) return;
    // Cancel any pending auto-advance so it can't fire after navigating backward.
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    resetAnswerUI();
    setQuestionObj(null);
    setIndex((i) => i - 1);
  };

  // ── Handle exit (before session is done) ──────────────────────────
  // On exit we update the session but do NOT complete it — this preserves
  // the ability to resume next time the user opens the quiz.
  const handleExit = async () => {
    if (!sessionDone && sessionIdRef.current && totalItemsRef.current > 0) {
      try {
        await updateQuizSession(sessionIdRef.current, {
          items_seen: totalItemsRef.current,
          items_correct: sessionRef.current.correct,
          current_index: Math.min(index, regularItemCountRef.current),
        });
      } catch (e) {
        console.error(e);
      }
    }
    if (onExit) onExit();
  };

  // ── Submit word MC answer ─────────────────────────────────────────
  const handleWordMCSubmit = async () => {
    if (!questionObj?.question || !selectedChoice || choiceSubmitted) return;
    const q = questionObj.question;

    const is_correct = selectedChoice === (q.correct_answer?.choice || "");

    setChoiceSubmitted(true);
    sessionRef.current.seen += 1;
    if (is_correct) sessionRef.current.correct += 1;

    try {
      await submitReviewAnswer({
        review_item_id: questionObj.review_item_id,
        question_type: q.question_type,
        is_correct,
        user_answer: { choice: selectedChoice },
        correct_answer: q.correct_answer || {},
        // Quiz is a pure test — never change the word's learned state.
        update_known: false,
      });
    } catch (e) {
      console.error("Submit failed", e);
    }

    saveToHistory(index, { questionObj, type: "word_meaning", userAnswer: selectedChoice, isCorrect: is_correct, wasSkipped: false });
    persistSessionProgress(index + 1);

    setFeedback({
      ok: is_correct,
      message: is_correct ? "✅ Correct!" : "❌ Not quite",
    });

    if (is_correct) {
      autoAdvanceTimerRef.current = setTimeout(goNext, 600);
    }
  };

  // ── Submit verb form MC answer ─────────────────────────────────────
  const handleVerbFormSubmit = () => {
    if (!questionObj?.question || !selectedChoice || choiceSubmitted) return;
    const q = questionObj.question;

    // Compare by form key (not text) to handle duplicate-text edge cases.
    const is_correct = selectedChoice === (q.correct_answer?.form || "");

    setChoiceSubmitted(true);
    verbFormScoreRef.current.seen += 1;
    if (is_correct) verbFormScoreRef.current.correct += 1;

    saveToHistory(index, { questionObj, type: "verb_forms", userAnswer: selectedChoice, isCorrect: is_correct, wasSkipped: false });
    persistSessionProgress(index + 1);

    setFeedback({
      ok: is_correct,
      message: is_correct ? "✅ Correct!" : "❌ Not quite",
    });

    // Don't auto-advance on the last verb form question — the user needs time
    // to press "Play all forms" before the session ends.
    if (is_correct && q.target_form !== "ism_mafool") {
      autoAdvanceTimerRef.current = setTimeout(goNext, 600);
    }
  };

  // ── Submit sentence answer ─────────────────────────────────────────
  const handleSentenceSubmit = async () => {
    if (!questionObj?.question || builtTokens.length === 0) return;
    const q = questionObj.question;

    const correctTokens = (q.correct_answer || {}).tokens || [];
    const is_correct = arraysEqual(builtTokens, correctTokens);

    sessionRef.current.seen += 1;
    if (is_correct) sessionRef.current.correct += 1;

    try {
      await submitReviewAnswer({
        review_item_id: questionObj.review_item_id,
        question_type: q.question_type,
        is_correct,
        user_answer: { tokens: builtTokens },
        correct_answer: q.correct_answer || {},
        // Quiz is a pure test — never change the word's learned state.
        update_known: false,
      });
    } catch (e) {
      console.error("Submit failed", e);
    }

    saveToHistory(index, { questionObj, type: "sentence_build", userAnswer: [...builtTokens], isCorrect: is_correct, wasSkipped: false });
    persistSessionProgress(index + 1);

    setFeedback({
      ok: is_correct,
      message: is_correct ? "✅ Correct!" : "❌ Not quite",
    });

    if (is_correct) {
      autoAdvanceTimerRef.current = setTimeout(goNext, 600);
    }
  };

  const q = questionObj?.question;
  // Read-only for answered questions in the already-answered range.
  // Pending entries (question loaded but not yet answered) are NOT read-only.
  const isViewingHistory = index < highWaterMark && !!questionHistory[index] && !questionHistory[index]?.pending;

  // ── Grammar drill phase ────────────────────────────────────────────
  if (grammarPhase === "active" && grammarItems && grammarItems.length > 0) {
    const gq = grammarItems[grammarIndex] || null;
    if (!gq) {
      setGrammarPhase("done");
      return null;
    }
    const isLastQuestion = grammarIndex + 1 >= grammarItems.length;
    const isCorrect = grammarSubmitted && grammarChoice === gq.correct_index;

    const handleGrammarSubmit = () => {
      if (grammarChoice === null) return;
      setGrammarSubmitted(true);
    };

    const handleGrammarNext = () => {
      // Record this grammar answer towards the combined quiz score.
      grammarScoreRef.current.seen += 1;
      if (grammarChoice === gq.correct_index) grammarScoreRef.current.correct += 1;

      const nextIdx = grammarIndex + 1;
      if (nextIdx < grammarItems.length) {
        setGrammarIndex(nextIdx);
        setGrammarChoice(null);
        setGrammarSubmitted(false);
      } else {
        setGrammarPhase("done");
        setGrammarIndex(0);
        setGrammarChoice(null);
        setGrammarSubmitted(false);
      }
    };

    return (
      <div className="border border-gold-200 rounded-xl bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 bg-gold-50 border-b border-gold-200 flex items-center justify-between">
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold text-gold-600 uppercase tracking-wide">Grammar Drill</span>
            {grammarInfo && (
              <span className="text-sm font-medium text-gold-600 mt-0.5">
                <span dir="rtl" lang="ar" className="font-arabic">{grammarInfo.arabic}</span>
                {" — "}{grammarInfo.name}
              </span>
            )}
            {grammarInfo?.explanation && (
              <span className="text-xs text-gold-600 mt-0.5">{grammarInfo.explanation}</span>
            )}
          </div>
          <div className="text-xs text-gold-600 shrink-0 ml-4">
            {grammarIndex + 1} / {grammarItems.length}
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Arabic sentence prompt (if any) */}
          {gq.prompt_ar && (
            <div
              dir="rtl"
              lang="ar"
              className="font-arabic text-2xl text-center py-3 bg-parchment-100 rounded-lg border border-parchment-200"
            >
              {gq.prompt_ar}
            </div>
          )}

          {/* English question */}
          <p className="text-sm font-medium text-ink-700">{gq.prompt_en}</p>

          {/* Answer choices */}
          <div className="space-y-2">
            {(gq.choices || []).map((choice, i) => {
              let className =
                "w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ";
              if (!grammarSubmitted) {
                className +=
                  grammarChoice === i
                    ? "surface-glass surface--active text-dt-gold-bright font-medium"
                    : "surface-glass surface--interactive text-dt-parchment";
              } else {
                if (i === gq.correct_index) {
                  className += "bg-dt-teal-bg border-dt-teal text-dt-teal font-medium";
                } else if (i === grammarChoice && i !== gq.correct_index) {
                  className += "bg-dt-rose-bg border-dt-rose text-dt-rose";
                } else {
                  className += "surface-glass text-dt-parchment-dim";
                }
              }
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => !grammarSubmitted && setGrammarChoice(i)}
                  disabled={grammarSubmitted}
                  className={className}
                >
                  {choice}
                </button>
              );
            })}
          </div>

          {/* Feedback + navigation */}
          {grammarSubmitted ? (
            <div className="space-y-3">
              <p className={`text-sm font-medium ${isCorrect ? "text-ink-700" : "text-red-700"}`}>
                {isCorrect
                  ? "✅ Correct!"
                  : `❌ The correct answer is: ${gq.choices[gq.correct_index]}`}
              </p>
              <button
                type="button"
                onClick={handleGrammarNext}
                className="w-full px-4 py-2.5 rounded-lg bg-gold-500 text-white text-sm font-medium hover:bg-gold-600 transition-colors"
              >
                {isLastQuestion ? "Continue to Vocabulary Quiz →" : "Next Question →"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleGrammarSubmit}
              disabled={grammarChoice === null}
              className="w-full px-4 py-2.5 rounded-lg bg-ink-700 text-white text-sm font-medium hover:bg-ink-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Check Answer
            </button>
          )}
        </div>

        {/* Exit link */}
        <div className="px-4 pb-3 flex justify-end">
          <button
            type="button"
            onClick={onExit}
            className="text-xs text-ink-300 hover:text-ink-500 transition-colors"
          >
            Exit Quiz
          </button>
        </div>
      </div>
    );
  }

  // ── Pre-quiz: checking for active session ─────────────────────────
  if (preQuizPhase === "checking") {
    return (
      <div className="border border-parchment-300 rounded-xl bg-white shadow-sm p-6 text-center">
        <p className="text-sm text-ink-400">Checking quiz progress…</p>
      </div>
    );
  }

  // ── Pre-quiz: resume dialog ────────────────────────────────────────
  if (preQuizPhase === "resume" && activeSessionData) {
    const resumeIndex = activeSessionData.current_index;
    const total = activeSessionData.item_order?.length || 0;
    return (
      <div className="border border-parchment-300 rounded-xl bg-white shadow-sm p-6">
        <div className="text-2xl mb-3 text-center">📖</div>
        <h2 className="text-lg font-semibold mb-1 text-center">Resume quiz?</h2>
        <p className="text-sm text-ink-400 text-center mb-6">
          You left off at question{" "}
          <span className="font-medium text-ink-700">{resumeIndex + 1}</span>
          {total > 0 && (
            <> of <span className="font-medium text-ink-700">{total}</span></>
          )}
          . Pick up where you left off or start over.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => { setPreQuizPhase(null); loadItems(activeSessionData); }}
            className="px-5 py-2.5 min-h-[44px] text-sm rounded-md bg-ink-600 text-white hover:bg-ink-700 font-medium"
          >
            Resume from Q{resumeIndex + 1}
          </button>
          <button
            onClick={async () => {
              await resetTopicProgress(topicId).catch(() => {});
              if (onProgress) onProgress();
              setPreQuizPhase(null);
              loadItems(null);
            }}
            className="px-5 py-2.5 min-h-[44px] text-sm rounded-md border border-parchment-300 hover:bg-parchment-100 text-ink-700"
          >
            Start from beginning
          </button>
        </div>
        <button
          onClick={onExit}
          className="mt-4 text-xs text-ink-300 hover:text-ink-500 block mx-auto"
        >
          Cancel
        </button>
      </div>
    );
  }

  // ── Pre-quiz: completed topic warning ─────────────────────────────
  if (preQuizPhase === "warn") {
    return (
      <div className="border border-amber-200 rounded-xl bg-amber-50 shadow-sm p-6">
        <div className="text-2xl mb-3 text-center">⚠️</div>
        <h2 className="text-lg font-semibold mb-1 text-center text-ink-800">
          This topic is completed
        </h2>
        <p className="text-sm text-amber-700 text-center mb-6">
          Your current score is{" "}
          <span className="font-bold">{topicBestScore}%</span>. Starting a new
          quiz will reset your progress to 0% until you complete it again.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={async () => {
              await resetTopicProgress(topicId).catch(() => {});
              if (onProgress) onProgress();
              setPreQuizPhase(null);
              loadItems(null);
            }}
            className="px-5 py-2.5 min-h-[44px] text-sm rounded-md bg-gold-500 text-white hover:bg-gold-600 font-medium"
          >
            Yes, start fresh
          </button>
          <button
            onClick={onExit}
            className="px-5 py-2.5 min-h-[44px] text-sm rounded-md border border-amber-300 hover:bg-parchment-100 text-ink-800"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Session complete screen ────────────────────────────────────────
  if (sessionDone && finalScore) {
    // `passed` is authoritative (backend gates topic completion on it). Fall
    // back to the score for older payloads without the flag.
    const passed = finalScore.passed ?? finalScore.score_percent >= 80;
    return (
      <div className="border border-parchment-300 rounded-xl bg-white shadow-sm p-6 text-center">
        <div className="text-4xl mb-3">{passed ? "🎉" : "💪"}</div>
        <h2 className="text-xl font-semibold mb-1">
          {passed ? "Topic complete!" : "Almost there"}
        </h2>
        <p className="text-sm text-ink-400 mb-1">
          You got{" "}
          <span className="font-medium text-ink-700">
            {finalScore.items_correct}
          </span>{" "}
          of{" "}
          <span className="font-medium">{finalScore.items_seen}</span> correct (
          {finalScore.score_percent}%)
        </p>
        {!passed && (
          <p className="text-sm text-ink-500 mb-4">
            You need <span className="font-semibold">80%</span> to complete this
            topic.{" "}
            {missedItemIds.length > 0
              ? "Retake just the ones you missed to get there."
              : "Give it another go."}
          </p>
        )}
        {passed && <div className="mb-4" />}

        <div className="w-full h-2 rounded-full bg-parchment-200 overflow-hidden mb-6">
          <div
            className={`h-full rounded-full transition-all duration-700 ${
              passed ? "bg-ink-500" : "bg-gold-400"
            }`}
            style={{ width: `${finalScore.score_percent}%` }}
          />
        </div>

        {/* Per-question breakdown — what you got right and wrong. Missed
            vocabulary words can be added to the Word Bank to learn again. */}
        {quizResults.length > 0 && (
          <div className="text-left mb-6">
            <h3 className="text-sm font-semibold text-ink-700 mb-2">Your answers</h3>
            <ul className="border border-parchment-200 rounded-lg overflow-hidden divide-y divide-parchment-200">
              {quizResults.map((r) => {
                const bs = r.wordId ? bankState[r.wordId] : undefined;
                const bankError = bs && typeof bs === "object" ? bs.error : null;
                return (
                  <li key={r.key} className="flex items-center gap-3 px-3 py-2 bg-white">
                    <span className="text-base leading-none" aria-hidden="true">
                      {r.isCorrect ? "✅" : "❌"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        {r.arabic && (
                          <span dir="rtl" className="text-base font-semibold text-ink-800 truncate">
                            {r.arabic}
                          </span>
                        )}
                        {r.english && (
                          <span className="text-xs text-ink-400 truncate">{r.english}</span>
                        )}
                      </div>
                      {r.transliteration && (
                        <div className="text-xs text-ink-300 truncate">{r.transliteration}</div>
                      )}
                      {/* User's answer, plus the correct one when they missed it. */}
                      <div className="mt-1 flex flex-col gap-0.5">
                        <span className={`text-xs ${r.isCorrect ? "text-emerald-700" : "text-red-600"}`}>
                          Your answer:{" "}
                          {r.wasSkipped ? (
                            "Skipped"
                          ) : r.userAnswerText ? (
                            <span dir={r.answerIsArabic ? "rtl" : "ltr"} className={r.answerIsArabic ? "font-arabic" : ""}>
                              {r.userAnswerText}
                            </span>
                          ) : (
                            "—"
                          )}
                        </span>
                        {!r.isCorrect && r.correctAnswerText && (
                          <span className="text-xs text-emerald-700">
                            Correct:{" "}
                            <span dir={r.answerIsArabic ? "rtl" : "ltr"} className={r.answerIsArabic ? "font-arabic" : ""}>
                              {r.correctAnswerText}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                    {!r.isCorrect && r.wordId && (
                      <div className="flex flex-col items-end gap-0.5 shrink-0">
                        {bs === "added" ? (
                          <span className="text-xs font-medium text-ink-500 whitespace-nowrap">
                            ✓ In Word Bank
                          </span>
                        ) : (
                          <button
                            onClick={() => handleAddToBank(r.wordId)}
                            disabled={bs === "adding"}
                            title="Add to Word Bank to learn it again"
                            className="px-2.5 py-1.5 min-h-[36px] text-xs font-semibold rounded-md border border-ink-200 text-ink-700 hover:bg-parchment-100 disabled:opacity-50 whitespace-nowrap"
                          >
                            {bs === "adding" ? "Adding…" : "+ Word Bank"}
                          </button>
                        )}
                        {bankError && (
                          <span className="text-[11px] text-red-600 whitespace-nowrap">{bankError}</span>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap gap-3 justify-center">
          {!passed && missedItemIds.length > 0 && (
            <button
              onClick={() => handleRetakeMissed(missedItemIds)}
              className="px-4 py-2 min-h-[44px] text-sm font-semibold rounded-md bg-ink-600 text-white hover:bg-ink-700"
            >
              Retake {missedItemIds.length} missed
            </button>
          )}
          <button
            onClick={() => {
              setSessionDone(false);
              setFinalScore(null);
              setPreQuizPhase("checking");
              setActiveSessionData(null);
            }}
            className="px-4 py-2 min-h-[44px] text-sm rounded-md border border-parchment-300 hover:bg-parchment-100"
          >
            {passed ? "Try again" : "Retake full quiz"}
          </button>
          <button
            onClick={onExit}
            className={`px-4 py-2 min-h-[44px] text-sm rounded-md ${
              passed
                ? "bg-ink-600 text-white hover:bg-ink-700"
                : "border border-parchment-300 hover:bg-parchment-100"
            }`}
          >
            Back to topics
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`border rounded-xl bg-white shadow-sm p-4 ${quizMode === "review" ? "border-gold-300" : "border-parchment-300"}`}>
      {/* Review Mode banner — prominent on both desktop and mobile */}
      {quizMode === "review" && (
        <div className="mb-3 px-3 py-2 rounded-lg flex items-center gap-2"
          style={{ background: "var(--gold-bg)", border: "1px solid var(--panel-line)" }}>
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="var(--gold-bright)" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          <span className="text-sm font-semibold tracking-wide" style={{ color: "var(--gold-bright)" }}>Review Mode</span>
          <span className="text-xs ml-auto hidden sm:block" style={{ color: "var(--gold-dim)" }}>
            Reinforcing what you've learned
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs text-ink-400">
            {quizMode === "review" ? "Review" : "Learn"}
          </div>
          {topicName && (
            <div className="text-sm font-semibold text-ink-800 mt-0.5">{topicName}</div>
          )}
          <div className="text-xs text-ink-400 mt-0.5">
            Question{" "}
            <span className="font-medium">{items.length ? (grammarItems || []).length + index + 1 : 0}</span>
            {" / "}
            <span className="font-medium">{(grammarItems || []).length + items.length}</span>
          </div>
        </div>

        <button
          onClick={handleExit}
          className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-parchment-300 hover:bg-parchment-100"
        >
          Exit
        </button>
      </div>

      {/* Previously-answered notice — shown when navigating back via Prev */}
      {isViewingHistory && (
        <div className="mt-3 px-3 py-1.5 rounded-md bg-gold-50 border border-gold-200 text-xs text-gold-600 flex items-center gap-2">
          <span>🔒</span>
          <span>Previously answered — read only. Skipped questions can be re-answered when you reach them again.</span>
        </div>
      )}

      {/* Session progress bar */}
      {items.length > 0 && (
        <div className="mt-3 h-1 rounded-full bg-parchment-200 overflow-hidden">
          <div
            className="h-full rounded-full bg-ink-400 transition-all duration-300"
            style={{ width: `${(((grammarItems || []).length + index + 1) / ((grammarItems || []).length + items.length)) * 100}%` }}
          />
        </div>
      )}

      {/* Body */}
      <div className="mt-4">
        {loadingItems && (
          <p className="text-sm text-ink-400">Loading items…</p>
        )}
        {itemsError && <p className="text-sm text-red-600">{itemsError}</p>}

        {!loadingItems && !itemsError && items.length === 0 && (
          <div className="text-sm text-ink-500 text-center py-6">
            <p className="mb-1">No items in this mode yet.</p>
            <p className="text-ink-300 text-xs">
              {quizMode === "review"
                ? "Answers need to be due for review."
                : "Topics need vocabulary loaded first."}
            </p>
          </div>
        )}

        {currentItem && (
          <div className="mt-1">
            {questionLoading && (
              <p className="text-sm text-ink-400">Generating question…</p>
            )}
            {questionError && (
              <p className="text-sm text-red-600">{questionError}</p>
            )}

            {q && (
              <div className="surface-glass rounded-lg p-4">

                {/* ══ WORD MEANING ════════════════════════════════════ */}
                {q.question_type === "word_meaning" && (
                  <>
                    <div className="text-xs text-ink-400 mb-2">
                      Choose the correct meaning
                    </div>

                    {/* Arabic prompt */}
                    <div className="flex items-center justify-center gap-2 py-3">
                      <div
                        className="text-4xl font-arabic text-center"
                        dir="rtl"
                      >
                        {q.prompt?.arabic}
                      </div>
                      {q.prompt?.arabic && isAudioPlaybackAllowed() && (
                        <button
                          type="button"
                          onClick={() => playArabic(q.prompt.arabic, `word-${currentItem?.id}`)}
                          disabled={ttsLoadingKey === `word-${currentItem?.id}`}
                          className="p-1 rounded text-ink-300 hover:text-ink-600 hover:bg-parchment-200 shrink-0 disabled:opacity-40"
                          title="Play pronunciation"
                          aria-label="Play pronunciation"
                        >
                          {ttsLoadingKey === `word-${currentItem?.id}` ? (
                            <svg className="w-5 h-5 animate-spin text-ink-500" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M6 4l14 8-14 8V4z" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>

                    {q.prompt?.transliteration && (
                      <div className="text-xs text-ink-300 text-center mb-3">
                        {q.prompt.transliteration}
                      </div>
                    )}

                    {/* Reveal panel — shown when user clicks "Reveal Answer" */}
                    {revealed && (
                      <div className="mb-3 px-4 py-3 rounded-md bg-ink-50 border border-ink-200 text-center">
                        <div className="text-xs text-ink-400 mb-0.5">
                          Correct answer
                        </div>
                        <div className="text-base font-semibold text-ink-800">
                          {q.correct_answer?.choice || "—"}
                        </div>
                        <button
                          onClick={() => setRevealed(false)}
                          className="mt-2 px-4 py-1.5 min-h-[36px] text-sm rounded-md bg-ink-600 text-white hover:bg-ink-700 transition-colors"
                        >
                          Got it
                        </button>
                      </div>
                    )}

                    {/* Multiple-choice options */}
                    <div className="grid gap-2">
                      {(q.choices || []).map((c) => {
                        const isSelected = selectedChoice === c;
                        const isCorrect = c === (q.correct_answer?.choice || "");
                        // After submission: colour correct green, wrong red
                        let cls =
                          "text-left px-3 py-2.5 rounded-md border text-sm transition-colors ";
                        if (choiceSubmitted) {
                          if (isCorrect) {
                            cls +=
                              "border-dt-teal bg-dt-teal-bg text-dt-teal font-medium";
                          } else if (isSelected) {
                            cls +=
                              "border-dt-rose bg-dt-rose-bg text-dt-rose";
                          } else {
                            cls += "surface-glass text-dt-parchment-dim";
                          }
                        } else if (isSelected) {
                          cls +=
                            "surface-glass surface--active text-dt-gold-bright font-medium";
                        } else {
                          cls +=
                            "surface-glass surface--interactive text-dt-parchment";
                        }

                        return (
                          <button
                            key={c}
                            disabled={choiceSubmitted}
                            onClick={() => {
                              playClickSound();
                              setSelectedChoice(c);
                            }}
                            className={cls}
                          >
                            {c}
                          </button>
                        );
                      })}
                    </div>

                    {/* Feedback — reserved slot so buttons don't shift when it appears */}
                    <div className="mt-3 min-h-[24px] text-sm">
                      {feedback && (
                        <span className={feedback.ok ? "text-ink-700" : "text-red-700"}>
                          {feedback.message}
                        </span>
                      )}
                    </div>

                    {/* Navigation bar — Prev always left, actions always right */}
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <button
                        onClick={goPrev}
                        disabled={index === 0}
                        className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-parchment-300 hover:bg-parchment-100 disabled:opacity-40"
                      >
                        Prev
                      </button>
                      <div className="flex gap-2 shrink-0">
                        {quizMode !== "review" && !choiceSubmitted && !revealed && (
                          <button
                            onClick={() => setRevealed(true)}
                            className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-parchment-300 bg-white hover:bg-parchment-100 text-ink-700"
                          >
                            Reveal Answer
                          </button>
                        )}
                        {selectedChoice && !choiceSubmitted && (
                          <button
                            onClick={handleWordMCSubmit}
                            className="px-3 py-1.5 min-h-[44px] text-sm rounded-md bg-ink-600 text-white hover:bg-ink-700"
                          >
                            Check
                          </button>
                        )}
                        {!choiceSubmitted && !isViewingHistory && (
                          <button
                            onClick={handleSkip}
                            className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-parchment-300 hover:bg-parchment-100"
                          >
                            Skip
                          </button>
                        )}
                        {choiceSubmitted && (!feedback?.ok || isViewingHistory) && (
                          <button
                            onClick={goNext}
                            disabled={isViewingHistory && index + 1 >= items.length}
                            className="px-3 py-1.5 min-h-[44px] text-sm rounded-md bg-ink-700 text-white hover:bg-ink-700 disabled:opacity-40"
                          >
                            Next →
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* ══ SENTENCE BUILD ══════════════════════════════════ */}
                {q.question_type === "sentence_build" && (() => {
                  const isCloze = q.mode === "cloze" && Array.isArray(q.pre_built) && Array.isArray(q.gap_indices);
                  const gapIndices = isCloze ? q.gap_indices : [];
                  const expectedCount = isCloze ? gapIndices.length : null;

                  const addToken = (tok, bankIdx) => {
                    if (isViewingHistory) return;
                    if (isCloze && builtTokens.length >= expectedCount) return;
                    playClickSound();
                    setBuiltTokens((prev) => [...prev, tok]);
                    setBuiltBankIndices((prev) => [...prev, bankIdx]);
                  };

                  const removeAt = (pos) => {
                    if (isViewingHistory) return;
                    setBuiltTokens((prev) => prev.filter((_, i) => i !== pos));
                    setBuiltBankIndices((prev) => prev.filter((_, i) => i !== pos));
                  };

                  const usedBankIdxSet = new Set(builtBankIndices);

                  return (
                  <>
                    <div className="text-xs text-ink-400 mb-2">
                      {isCloze ? "Fill in the missing words" : "Build the Arabic sentence"}
                    </div>

                    <div className="text-sm text-ink-700 mb-3">
                      <span className="text-ink-400">English:</span>{" "}
                      <span className="font-medium">{q.prompt?.english}</span>
                    </div>

                    {/* Reveal panel for sentence */}
                    {sentenceRevealed && (
                      <div className="mb-3 px-4 py-3 rounded-md bg-ink-50 border border-ink-200">
                        <div className="text-xs text-ink-400 mb-1">
                          {isCloze ? "Correct missing words (in order)" : "Correct answer"}
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            className="font-arabic text-2xl text-right text-ink-800 leading-relaxed flex-1"
                            dir="rtl"
                          >
                            {(q.correct_answer?.tokens || []).join(" ")}
                          </div>
                          {(q.correct_answer?.tokens || []).length > 0 && isAudioPlaybackAllowed() && (
                            <button
                              type="button"
                              onClick={() => playArabic(
                                (q.correct_answer.tokens || []).join(" "),
                                `sentence-${currentItem?.id}`
                              )}
                              disabled={ttsLoadingKey === `sentence-${currentItem?.id}`}
                              className="p-1 rounded text-ink-300 hover:text-ink-600 hover:bg-ink-100 shrink-0 disabled:opacity-40"
                              title="Play pronunciation"
                              aria-label="Play pronunciation"
                            >
                              {ttsLoadingKey === `sentence-${currentItem?.id}` ? (
                                <svg className="w-5 h-5 animate-spin text-ink-500" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M6 4l14 8-14 8V4z" />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => setSentenceRevealed(false)}
                          className="mt-2 px-4 py-1.5 min-h-[36px] text-sm rounded-md bg-ink-600 text-white hover:bg-ink-700 transition-colors"
                        >
                          Got it
                        </button>
                      </div>
                    )}

                    {/* Sentence area */}
                    {isCloze ? (
                      <div
                        className="min-h-[56px] border border-dt-line rounded-md bg-dt-forest-soft p-2 text-right font-arabic text-2xl leading-relaxed flex flex-wrap gap-2 justify-end"
                        dir="rtl"
                      >
                        {q.pre_built.map((tok, idx) => {
                          if (tok !== null && tok !== undefined) {
                            return (
                              <span key={`pb-${idx}`} className="inline-block">
                                {tok}
                              </span>
                            );
                          }
                          // Gap slot: figure out which gap (in order) and what fills it
                          const gapOrder = gapIndices.indexOf(idx);
                          const filledTok = builtTokens[gapOrder];
                          return (
                            <button
                              key={`gap-${idx}`}
                              type="button"
                              disabled={isViewingHistory || filledTok === undefined}
                              onClick={() => removeAt(gapOrder)}
                              className={
                                filledTok !== undefined
                                  ? "px-3 py-0.5 rounded-md bg-dt-gold-bg text-dt-gold-bright border border-dt-gold hover:brightness-110 disabled:opacity-70"
                                  : "px-6 py-0.5 rounded-md border-2 border-dashed border-dt-line text-dt-parchment-dim"
                              }
                            >
                              {filledTok !== undefined ? filledTok : "ـــ"}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div
                        className="min-h-[56px] border border-dt-line rounded-md bg-dt-forest-soft p-2 text-right font-arabic text-2xl leading-relaxed flex flex-wrap gap-2 justify-end"
                        dir="rtl"
                      >
                        {builtTokens.length === 0 ? (
                          <span className="text-ink-300 text-sm font-sans">
                            Tap words below to build the sentence…
                          </span>
                        ) : (
                          builtTokens.map((t, idx) => (
                            <button
                              key={`bt-${t}-${idx}`}
                              type="button"
                              disabled={isViewingHistory}
                              onClick={() => removeAt(idx)}
                              className="px-2 py-0.5 rounded-md bg-dt-gold-bg text-dt-gold-bright border border-dt-gold hover:brightness-110 disabled:opacity-70"
                            >
                              {t}
                            </button>
                          ))
                        )}
                      </div>
                    )}

                    {/* Word bank — used chips greyed out */}
                    <div className="mt-3 flex flex-wrap gap-2 justify-end">
                      {(q.word_bank || []).map((tok, idx) => {
                        const used = usedBankIdxSet.has(idx);
                        const disabled = isViewingHistory || used ||
                          (isCloze && builtTokens.length >= expectedCount);
                        return (
                          <button
                            key={`${tok}-${idx}`}
                            disabled={disabled}
                            className={
                              "px-3 py-1.5 min-h-[44px] rounded-md text-xl font-arabic " +
                              (used
                                ? "border border-dt-line bg-dt-forest-soft text-dt-parchment-dim line-through cursor-not-allowed"
                                : "surface-glass surface--interactive text-dt-parchment disabled:opacity-40 disabled:cursor-not-allowed")
                            }
                            dir="rtl"
                            onClick={() => addToken(tok, idx)}
                          >
                            {tok}
                          </button>
                        );
                      })}
                    </div>

                      {/* Clear / Undo / Reveal — hidden when viewing history */}
                      {!isViewingHistory && (
                        <div className="mt-2 flex gap-2 flex-wrap">
                          <button
                            className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-parchment-300 hover:bg-parchment-100"
                            onClick={() => { setBuiltTokens([]); setBuiltBankIndices([]); }}
                          >
                            Clear
                          </button>
                          <button
                            className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-parchment-300 hover:bg-parchment-100 disabled:opacity-50"
                            onClick={() => {
                              setBuiltTokens((prev) => prev.slice(0, -1));
                              setBuiltBankIndices((prev) => prev.slice(0, -1));
                            }}
                            disabled={builtTokens.length === 0}
                          >
                            Undo
                          </button>
                          {quizMode !== "review" && !sentenceRevealed && (
                            <button
                              className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-parchment-300 bg-white hover:bg-parchment-100 text-ink-700 ml-auto"
                              onClick={() => setSentenceRevealed(true)}
                            >
                              Reveal Answer
                            </button>
                          )}
                        </div>
                      )}

                    {/* Feedback — reserved slot so buttons don't shift */}
                    <div className="mt-3 min-h-[24px] text-sm">
                      {feedback && (
                        <span className={feedback.ok ? "text-ink-700" : "text-red-700"}>
                          {feedback.message}
                        </span>
                      )}
                    </div>

                    {/* Navigation bar — Prev always left, actions always right */}
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <button
                        onClick={goPrev}
                        disabled={index === 0}
                        className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-parchment-300 hover:bg-parchment-100 disabled:opacity-40"
                      >
                        Prev
                      </button>
                      <div className="flex gap-2 shrink-0">
                        {!isViewingHistory && (
                          <>
                            <button
                              onClick={handleSentenceSubmit}
                              disabled={builtTokens.length === 0 || (isCloze && builtTokens.length !== expectedCount)}
                              className="px-3 py-1.5 min-h-[44px] text-sm rounded-md bg-ink-600 text-white hover:bg-ink-700 disabled:opacity-50"
                            >
                              Check
                            </button>
                            <button
                              onClick={handleSkip}
                              className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-parchment-300 hover:bg-parchment-100"
                            >
                              Skip
                            </button>
                          </>
                        )}
                        {isViewingHistory && (
                          <button
                            onClick={goNext}
                            disabled={index + 1 >= items.length}
                            className="px-3 py-1.5 min-h-[44px] text-sm rounded-md bg-ink-700 text-white hover:bg-ink-700 disabled:opacity-40"
                          >
                            Next →
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                  );
                })()}

              </div>
            )}

            {/* ── Verb form question ─────────────────────────────── */}
            {q?.question_type === "verb_forms" && (
              <div className="flex flex-col gap-4">
                {/* Verb form label */}
                <div className="text-center">
                  <div className="text-xs uppercase tracking-wide text-ink-400 mb-1">
                    {q.target_label_en}
                  </div>
                  <div className="text-lg font-arabic text-ink-700" dir="rtl">
                    {q.target_label_ar}
                  </div>
                </div>

                {/* Arabic prompt — past tense form */}
                <div className="text-center">
                  <div className="text-xs text-ink-300 mb-1">Past tense form</div>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-3xl font-arabic font-bold" dir="rtl">
                      {q.past}
                    </span>
                    <button
                      className="text-ink-300 hover:text-ink-500"
                      title="Play pronunciation"
                      onClick={() =>
                        synthesizeSpeech(q.past, "").then((data) => {
                          if (data?.audio_content) {
                            const audio = new Audio(`data:audio/mp3;base64,${data.audio_content}`);
                            audio.play();
                          }
                        }).catch(() => {})
                      }
                    >
                      ▶
                    </button>
                  </div>
                  {q.past_transliteration && (
                    <div className="text-sm text-ink-300 mt-1">{q.past_transliteration}</div>
                  )}
                </div>

                {/* 5 MC choice buttons */}
                <div className="flex flex-col gap-2">
                  {(q.choices || []).map((choice) => {
                    const isSelected = selectedChoice === choice.form;
                    const isCorrect = choice.form === (q.correct_answer?.form || "");
                    // Same selection treatment as the word-meaning options above:
                    // selected → gold ring, correct → teal, wrong → rose.
                    let btnCls =
                      "w-full px-4 py-3 min-h-[52px] rounded-lg border text-right font-arabic text-lg transition-colors ";
                    if (!choiceSubmitted) {
                      btnCls += isSelected
                        ? "surface-glass surface--active text-dt-gold-bright font-medium"
                        : "surface-glass surface--interactive text-dt-parchment";
                    } else {
                      if (isCorrect) btnCls += "border-dt-teal bg-dt-teal-bg text-dt-teal font-medium";
                      else if (isSelected && !isCorrect) btnCls += "border-dt-rose bg-dt-rose-bg text-dt-rose";
                      else btnCls += "surface-glass text-dt-parchment-dim opacity-60";
                    }
                    return (
                      <button
                        key={choice.form}
                        dir="rtl"
                        className={btnCls}
                        disabled={choiceSubmitted || isViewingHistory}
                        onClick={() => !choiceSubmitted && setSelectedChoice(choice.form)}
                      >
                        {choice.text}
                      </button>
                    );
                  })}
                </div>

                {/* Feedback */}
                <div className="min-h-[24px] text-sm">
                  {feedback && (
                    <span className={feedback.ok ? "text-ink-700" : "text-red-700"}>
                      {feedback.message}
                    </span>
                  )}
                </div>

                {/* Play all forms — shown only on the last verb form question after answering */}
                {q.target_form === "ism_mafool" && choiceSubmitted && !isViewingHistory && isAudioPlaybackAllowed() && (
                  <button
                    onClick={() => {
                      // Build ordered list: past → present → imperative → masdar → ism_faail → ism_mafool
                      const FORM_ORDER = ["present", "imperative", "masdar", "ism_faail", "ism_mafool"];
                      const byForm = Object.fromEntries((q.choices || []).map((c) => [c.form, c.text]));
                      const texts = [q.past, ...FORM_ORDER.map((f) => byForm[f]).filter(Boolean)];
                      playAllVerbForms(texts);
                    }}
                    disabled={isPlayingAllForms}
                    className="dt-ghost-btn w-full py-2.5 rounded-lg text-sm font-medium disabled:opacity-60"
                  >
                    {isPlayingAllForms ? "▶ Playing…" : "▶ Play all forms"}
                  </button>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={goPrev}
                    disabled={index === 0}
                    className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-parchment-300 hover:bg-parchment-100 disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <div className="flex gap-2 shrink-0">
                    {!isViewingHistory && !choiceSubmitted && (
                      <>
                        <button
                          onClick={handleVerbFormSubmit}
                          disabled={!selectedChoice}
                          className="px-3 py-1.5 min-h-[44px] text-sm rounded-md bg-ink-600 text-white hover:bg-ink-700 disabled:opacity-50"
                        >
                          Check
                        </button>
                        <button
                          onClick={handleSkip}
                          className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-parchment-300 hover:bg-parchment-100"
                        >
                          Skip
                        </button>
                      </>
                    )}
                    {!isViewingHistory && choiceSubmitted && (
                      <button
                        onClick={goNext}
                        className="px-3 py-1.5 min-h-[44px] text-sm rounded-md bg-ink-700 text-white hover:bg-ink-700"
                      >
                        Next →
                      </button>
                    )}
                    {isViewingHistory && (
                      <button
                        onClick={goNext}
                        disabled={index + 1 >= items.length}
                        className="px-3 py-1.5 min-h-[44px] text-sm rounded-md bg-ink-700 text-white hover:bg-ink-700 disabled:opacity-40"
                      >
                        Next →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}